import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './SharedAddresses.css';

const API_URL  = process.env.REACT_APP_API_URL;
const ECOM_URL  = process.env.REACT_APP_ECOM_URL || 'https://prebook.thenewluxury.in';
const encodeRef = (payload) => btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
const buildAddressLink = (row) => {
  const hasPincode = /\d{6}/.test(row.address || '');
  const fullAddress = hasPincode || !row.pincode ? row.address : `${row.address} - ${row.pincode}`;
  const payload = { aid: row.id, aa: fullAddress, rid: row.owner_id, rn: row.owner_name };
  return `${ECOM_URL}/register?ref=${encodeURIComponent(encodeRef(payload))}`;
};

export default function SharedAddresses() {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${user?.token}` } });

  const [addresses, setAddresses] = useState([]);
  const [cwpUsers, setCwpUsers]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  // modal state
  const [modal, setModal]         = useState(null); // null | 'create' | { ...row }
  const [form, setForm]           = useState({ name: '', address: '', pincode: '', waive_delivery_fee: false, owner_id: '' });
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState('');
  const [copied, setCopied]       = useState(null);

  // drill-down
  const [customers, setCustomers]     = useState(null);
  const [custLoading, setCustLoading] = useState(false);
  const [expandedId, setExpandedId]   = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/addresses/shared`, auth())
      .then(r => { setAddresses(r.data); setLoading(false); })
      .catch(() => { setError('Failed to load shared addresses.'); setLoading(false); });
  };

  useEffect(() => {
    load();
    axios.get(`${API_URL}/addresses/cwp-users`, auth())
      .then(r => setCwpUsers(r.data))
      .catch(() => {});
  }, []); // eslint-disable-line

  const openCreate = () => {
    setForm({ name: '', address: '', pincode: '', waive_delivery_fee: false, owner_id: '' });
    setFormError('');
    setModal('create');
  };

  const openEdit = (row) => {
    setForm({
      name:               row.name || '',
      address:            row.address,
      pincode:            row.pincode,
      waive_delivery_fee: !!row.waive_delivery_fee,
      owner_id:           row.owner_id ? String(row.owner_id) : '',
    });
    setFormError('');
    setModal(row);
  };

  const closeModal = () => { setModal(null); setFormError(''); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.address.trim() || !form.pincode.trim()) {
      setFormError('Name, address and pincode are required.'); return;
    }
    setSaving(true); setFormError('');
    const payload = {
      ...form,
      owner_id: form.owner_id ? parseInt(form.owner_id, 10) : null,
    };
    try {
      if (modal === 'create') {
        await axios.post(`${API_URL}/addresses/shared`, payload, auth());
      } else {
        await axios.put(`${API_URL}/addresses/${modal.id}`, payload, auth());
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = (row) => {
    navigator.clipboard.writeText(buildAddressLink(row)).then(() => {
      setCopied(row.id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const toggleStatus = async (row) => {
    const next = row.status === 'A' ? 'I' : 'A';
    try {
      await axios.put(`${API_URL}/addresses/${row.id}/status`, { status: next }, auth());
      load();
    } catch { setError('Failed to update status.'); }
  };

  const toggleCustomers = async (row) => {
    if (expandedId === row.id) { setExpandedId(null); setCustomers(null); return; }
    setExpandedId(row.id);
    setCustLoading(true);
    try {
      const r = await axios.get(`${API_URL}/addresses/shared/${row.id}/customers`, auth());
      setCustomers(r.data);
    } catch { setCustomers([]); }
    finally { setCustLoading(false); }
  };

  return (
    <div className="sa-page">
      <div className="sa-header">
        <h2 className="sa-title">Shared Addresses</h2>
        <button className="sa-btn sa-btn--primary" onClick={openCreate}>+ New Shared Location</button>
      </div>

      {error && <p className="sa-error">{error}</p>}

      {loading ? (
        <p className="sa-loading">Loading…</p>
      ) : (
        <div className="sa-table-wrap">
          <table className="sa-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>Pincode</th>
                <th>Owner</th>
                <th>Free Delivery</th>
                <th className="sa-num">Customers</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {addresses.length === 0 && (
                <tr><td colSpan={8} className="sa-empty">No shared addresses yet.</td></tr>
              )}
              {addresses.map(row => (
                <>
                  <tr key={row.id} className={row.status === 'I' ? 'sa-row-inactive' : ''}>
                    <td className="sa-name">{row.name}</td>
                    <td><div className="sa-address">{row.address}</div></td>
                    <td>{row.pincode}</td>
                    <td className="sa-owner">{row.owner_name || <span className="sa-none">—</span>}</td>
                    <td>
                      {row.waive_delivery_fee
                        ? <span className="sa-badge sa-badge--free">Yes</span>
                        : <span className="sa-badge sa-badge--no">No</span>}
                    </td>
                    <td className="sa-num">
                      <button className="sa-link-btn" onClick={() => toggleCustomers(row)}>
                        {row.linked_customers} {expandedId === row.id ? '▲' : '▼'}
                      </button>
                    </td>
                    <td>
                      <span className={`sa-status sa-status--${row.status === 'A' ? 'active' : 'inactive'}`}>
                        {row.status === 'A' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="sa-actions">
                        <button className="sa-btn sa-btn--sm" onClick={() => openEdit(row)}>Edit</button>
                        <button className="sa-btn sa-btn--sm sa-btn--ghost" onClick={() => toggleStatus(row)}>
                          {row.status === 'A' ? 'Deactivate' : 'Activate'}
                        </button>
                        {row.owner_id && (
                          <button className="sa-btn sa-btn--sm sa-btn--ghost" onClick={() => copyLink(row)}>
                            {copied === row.id ? 'Copied!' : 'Copy Link'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === row.id && (
                    <tr key={`${row.id}-customers`} className="sa-expand-row">
                      <td colSpan={8}>
                        {custLoading ? (
                          <p className="sa-loading">Loading customers…</p>
                        ) : customers?.length === 0 ? (
                          <p className="sa-expand-empty">No customers linked to this address.</p>
                        ) : (
                          <table className="sa-cust-table">
                            <thead>
                              <tr><th>Customer</th><th>Phone</th><th>Label</th><th>Notes</th><th>Default?</th></tr>
                            </thead>
                            <tbody>
                              {customers?.map(c => (
                                <tr key={c.id}>
                                  <td>{c.customer_name}</td>
                                  <td>{c.phone || '—'}</td>
                                  <td>{c.label}</td>
                                  <td>{c.notes || '—'}</td>
                                  <td>{c.is_default ? 'Yes' : '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit modal */}
      {modal && (
        <div className="sa-modal-overlay" onClick={closeModal}>
          <div className="sa-modal" onClick={e => e.stopPropagation()}>
            <h3 className="sa-modal-title">{modal === 'create' ? 'New Shared Location' : 'Edit Shared Location'}</h3>

            {formError && <p className="sa-error" style={{ marginBottom: 10 }}>{formError}</p>}

            {[
              { key: 'name',    label: 'Location Name', placeholder: 'e.g. WeWork Koramangala' },
              { key: 'pincode', label: 'Pincode',       placeholder: '560034' },
            ].map(({ key, label, placeholder }) => (
              <div key={key} className="sa-field">
                <label className="sa-label">{label}</label>
                <input className="sa-input" value={form[key]} placeholder={placeholder}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} disabled={saving} />
              </div>
            ))}

            <div className="sa-field">
              <label className="sa-label">Address</label>
              <textarea className="sa-input sa-textarea" rows={3} value={form.address}
                placeholder="Full address text"
                onChange={e => setForm(f => ({ ...f, address: e.target.value }))} disabled={saving} />
            </div>

            <div className="sa-field">
              <label className="sa-label">Owner</label>
              <select className="sa-input" value={form.owner_id}
                onChange={e => setForm(f => ({ ...f, owner_id: e.target.value }))} disabled={saving}>
                <option value="">— No owner —</option>
                {cwpUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.display_name}</option>
                ))}
              </select>
            </div>

            <div className="sa-field sa-field--check">
              <label className="sa-check-label">
                <input type="checkbox" checked={form.waive_delivery_fee}
                  onChange={e => setForm(f => ({ ...f, waive_delivery_fee: e.target.checked }))}
                  disabled={saving} />
                Waive delivery fee for this location
              </label>
            </div>

            <div className="sa-modal-actions">
              <button className="sa-btn sa-btn--ghost" onClick={closeModal} disabled={saving}>Cancel</button>
              <button className="sa-btn sa-btn--primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : (modal === 'create' ? 'Create' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
